import {
  Button,
  Combobox,
  DatePicker,
  Form,
  FormControl,
  FormField,
  FormInput,
  FormItem,
  FormLabel,
  FormMessage,
  Heading,
  IconRefresh,
  Label,
  RadioGroup,
  RadioGroupItem,
  Text,
} from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { formatISO, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import FormError from "@/components/FormError";
import countries from "@/data/countries";

import useBroadcastForm from "./hooks/useBroadcastForm";
import useBroadcastMutation from "./hooks/useBroadcastMutation";

function BroadcastsCreateUpdate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: siteConfig } = useSiteConfigAuth();

  if (siteConfig?.disable_broadcasts) {
    void navigate({ to: "/" });
  }

  const {
    form,
    isUpdate,
    broadcastId,
    myAccount,
    profile,
    broadcasts,
    broadcast,
    refetchBroadcast,
    songs,
  } = useBroadcastForm();

  const { pending, mutationErrorMessage, addBroadcast, updateBroadcast } = useBroadcastMutation({
    form,
    broadcastId,
    broadcasts,
    refetchBroadcast,
    myAccount,
    songs,
    profile,
  });

  if ((broadcastId != null) && broadcast.data?.length === 0) {
    void navigate({ to: "/broadcasts" });
  }

  const str = (s: string | null | undefined): string => s ?? "";

  const songOptions = songs.data?.works.map(w => ({
    label: w.title,
    value: (w.workcode ?? w.tc),
  })) ?? [];

  const countryOptions = countries.map(c => ({
    label: t(`countries:${c.name.replace(" ", "-")}`),
    value: c.value,
  }));

  return (
    <div className="flex-1 order-last xl:w-full bg-layer-raised min-h-50 lg:min-h-screen-minus-header">
      {songs.isLoading && (
        <div className="flex items-center justify-center py-12">
          <IconRefresh size="lg" className="animate-spin text-ink-accent" />
        </div>
      )}

      {songs.isSuccess && (
        <Form {...form}>
          <div className="flex items-baseline gap-5 mb-6">
            <Heading as="h3">
              {isUpdate
                ? t("other:update-broadcast", "Update Broadcast")
                : t("other:add-broadcast", "Add Broadcast")}
            </Heading>
          </div>

          <div className="bg-white shadow-sm rounded-lg p-4 md:px-10 md:py-10">
            <div className="flex w-full flex-col gap-6">

              {/* Song section */}
              <div className="flex flex-col gap-1">
                <Text as="subtitle">{t("other:broadcast-song-header", "Song")}</Text>
                <Text>{t("other:broadcast-song-subtitle", "Search for the song that was broadcast")}</Text>
                <div className="flex flex-col gap-2">
                  <FormField
                    control={form.control}
                    name="song"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>
                          {str(t("other:select-a-song-placeholder", "Select a song"))}
                        </FormLabel>
                        <FormControl>
                          <Combobox
                            data={songOptions}
                            value={field.value ?? ""}
                            onValueChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <Text as="subtitle">{t("other:broadcast-details-header", "Broadcast details")}</Text>
                <Text>{t("other:broadcast-details-subtitle", "Enter the medium, station and program name")}</Text>
                <div className="flex flex-col gap-2">
                  <FormField
                    control={form.control}
                    name="medium"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>
                          {str(t("other:choose-medium", "Medium"))}
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            value={field.value ?? ""}
                            onValueChange={field.onChange}
                            className="flex flex-row gap-4"
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem id="mediumRadio" value="Radio" />
                              <Label htmlFor="mediumRadio">
                                {str(t("other:radio", "Radio"))}
                              </Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem id="mediumTV" value="TV" />
                              <Label htmlFor="mediumTV">
                                {str(t("other:tv", "TV"))}
                              </Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="station"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>
                          {str(t("other:station-channel-placeholder", "Station / Channel"))}
                        </FormLabel>
                        <FormControl>
                          <FormInput
                            id="txtBroadcastStation"
                            placeholder={str(t("other:station-channel-placeholder", "Station / Channel"))}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="program"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {str(t("other:program-placeholder", "Program"))}
                        </FormLabel>
                        <FormControl>
                          <FormInput
                            id="txtBroadcastProgram"
                            placeholder={str(t("other:program-placeholder", "Program"))}
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <Text as="subtitle">{t("other:broadcast-location-date-header", "Location & date")}</Text>
                <Text>{t("other:broadcast-location-date-subtitle", "Enter the country and date the broadcast aired")}</Text>
                <div className="flex flex-col gap-2">
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>
                          {str(t("other:select-country-placeholder", "Select country"))}
                        </FormLabel>
                        <FormControl>
                          <Combobox
                            data={countryOptions}
                            value={field.value ?? ""}
                            onValueChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="dateAired"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>
                          {str(t("other:date-aired-placeholder", "Date aired"))}
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            placeholder={str(t("other:date-aired-placeholder", "Date aired"))}
                            value={field.value ? parseISO(field.value) : undefined}
                            onChange={date =>
                              field.onChange(
                                date ? formatISO(date, { representation: "date" }) : undefined,
                              )}
                            disabledDays={{ after: new Date() }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <Text as="subtitle">{t("other:broadcast-evidence-header", "Evidence")}</Text>
                <Text>{t("other:broadcast-evidence-subtitle", "Provide supporting evidence for this broadcast claim")}</Text>
                <div className="flex flex-col gap-2">
                  <FormField
                    control={form.control}
                    name="evidence"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>
                          {str(t("other:evidence-placeholder", "Evidence"))}
                        </FormLabel>
                        <FormControl>
                          <textarea
                            id="txtBroadcastEvidence"
                            placeholder={str(t("other:evidence-placeholder", "Evidence"))}
                            rows={4}
                            className="w-full font-sentric-alt font-medium text-sm rounded-lg px-2 py-1.5 ring-1 ring-canvas-inverse hover:ring-2 hover:ring-stroke-primary focus-visible:ring-2 focus-visible:ring-stroke-primary focus-visible:outline-none placeholder:text-black/50 transition ease-out duration-200 resize-none"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <FormError
                mutationError={mutationErrorMessage}
                validationError={form.formState.errors.root?.message}
              />
              <div className="flex justify-end">
                <Button
                  id={isUpdate ? "btnBroadcastEdit" : "btnBroadcastAdd"}
                  variant="primary"
                  label={str(isUpdate
                    ? t("other:update-broadcast", "Update Broadcast")
                    : t("other:add-broadcast", "Add Broadcast"))}
                  onClick={() => { void (isUpdate ? updateBroadcast() : addBroadcast()); }}
                  disabled={pending}
                  icon={pending ? <IconRefresh className="animate-spin" /> : undefined}
                />
              </div>

            </div>
          </div>
        </Form>
      )}
    </div>
  );
}

export default BroadcastsCreateUpdate;
